import { useId } from 'react'
import type { JSX, PropsWithChildren, ReactNode } from 'react'

import {
  ChannelAvatar,
  Heading,
  MessagesSquareIcon,
  PageHeader,
  PageLayout,
  Skeleton,
  Tabs,
  Text,
  ThumbsUpIcon,
} from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import { EmptyStateBanner } from '~publish/components/EmptyStateBanner'
import { SectionAvatar } from '~publish/components/SectionAvatar'

import styles from './CommunityEmptyState.module.css'

const COMMENT_PREVIEW_CHANNELS: { id: string; channel: ChannelType }[] = [
  { id: 'instagram-first', channel: 'instagram' },
  { id: 'linkedin-first', channel: 'linkedin' },
  { id: 'tiktok', channel: 'tiktok' },
  { id: 'facebook', channel: 'facebook' },
  { id: 'instagram-second', channel: 'instagram' },
  { id: 'linkedin-second', channel: 'linkedin' },
]

const MENTION_PREVIEW_CHANNELS: { id: string; channel: ChannelType }[] = [
  { id: 'linkedin', channel: 'linkedin' },
  { id: 'instagram', channel: 'instagram' },
  { id: 'facebook', channel: 'facebook' },
]

function PreviewLines(): JSX.Element {
  return (
    <div className={styles.previewLines}>
      <Skeleton height={6} className={styles.placeholder} />
      <Skeleton height={6} className={styles.placeholder} />
      <Skeleton width="60%" height={6} className={styles.placeholder} />
    </div>
  )
}

function CommentsPreview(): JSX.Element {
  return (
    <div className={styles.preview} aria-hidden>
      {COMMENT_PREVIEW_CHANNELS.map(({ id, channel }) => (
        <div key={id} className={styles.comment}>
          <ChannelAvatar channel={channel} size="small" alt="" />
          <div className={styles.commentContent}>
            <Skeleton width={72} height={8} className={styles.placeholder} />
            <PreviewLines />
            <ThumbsUpIcon
              size="small"
              color="subtle"
              className={styles.reaction}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

function MentionsPreview(): JSX.Element {
  return (
    <div className={styles.preview} aria-hidden>
      {MENTION_PREVIEW_CHANNELS.map(({ id, channel }) => (
        <div key={id} className={styles.mention}>
          <div className={styles.mentionSource}>
            <ChannelAvatar channel={channel} size="small" alt="" />
            <Skeleton width={72} height={8} className={styles.placeholder} />
          </div>
          <div className={styles.mentionContent}>
            <div className={styles.quotedMention}>
              <Skeleton
                rounded
                width={24}
                height={24}
                className={styles.placeholder}
              />
              <div className={styles.quotedMentionContent}>
                <Skeleton
                  width={88}
                  height={8}
                  className={styles.placeholder}
                />
                <PreviewLines />
              </div>
            </div>
            <ThumbsUpIcon
              size="small"
              color="subtle"
              className={styles.reaction}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export type CommunityEmptyStateTab = 'comments' | 'mentions'

type CommunityEmptyStateProps = PropsWithChildren<{
  comments?: ReactNode
  mentions?: ReactNode
  commentsTabIndicator?: ReactNode
  mentionsTabIndicator?: ReactNode
  tabActions?: ReactNode
  previewBar?: ReactNode
  tab?: CommunityEmptyStateTab
  onTabChange?: (tab: string) => void
}>

function CommunityEmptyStateRoot({
  children,
  comments,
  mentions,
  commentsTabIndicator,
  mentionsTabIndicator,
  tabActions,
  previewBar,
  tab,
  onTabChange,
}: CommunityEmptyStateProps): JSX.Element {
  const headingId = useId()
  const tabList = (
    <Tabs.TabList>
      <Tabs.Tab value="comments">
        Comments
        {commentsTabIndicator}
      </Tabs.Tab>
      <Tabs.Tab value="mentions">
        Mentions
        {mentionsTabIndicator}
      </Tabs.Tab>
    </Tabs.TabList>
  )

  return (
    <PageLayout>
      <PageHeader separator={false}>
        <PageHeader.Row>
          <PageHeader.TitleGroup>
            <PageHeader.TitleLeading>
              <SectionAvatar>
                <MessagesSquareIcon size={24} />
              </SectionAvatar>
            </PageHeader.TitleLeading>
            <PageHeader.Title>Community</PageHeader.Title>
          </PageHeader.TitleGroup>
        </PageHeader.Row>
      </PageHeader>
      <PageLayout.Container className={styles.content}>
        <EmptyStateBanner aria-labelledby={headingId}>
          <EmptyStateBanner.Content>
            <Heading as="h2" size="sm" id={headingId}>
              Every comment in one inbox
            </Heading>
            <Text color="subtle">
              Connect a channel to unlock your comment inbox.
            </Text>
            {children}
          </EmptyStateBanner.Content>
          <EmptyStateBanner.Decorations>
            <EmptyStateBanner.Decoration>💬</EmptyStateBanner.Decoration>
            <EmptyStateBanner.Decoration>❤️</EmptyStateBanner.Decoration>
            <EmptyStateBanner.Decoration>🙌</EmptyStateBanner.Decoration>
            <EmptyStateBanner.Decoration>📥</EmptyStateBanner.Decoration>
          </EmptyStateBanner.Decorations>
        </EmptyStateBanner>
        <Tabs
          defaultValue="comments"
          value={tab}
          onChange={onTabChange}
          className={styles.inbox}
        >
          {tabActions ? (
            <div className={styles.tabRow}>
              {tabList}
              <div className={styles.tabActions}>{tabActions}</div>
            </div>
          ) : (
            tabList
          )}
          <Tabs.Panel value="comments" className={styles.panel}>
            {comments ?? <CommentsPreview />}
          </Tabs.Panel>
          <Tabs.Panel value="mentions" className={styles.panel}>
            {mentions ?? <MentionsPreview />}
          </Tabs.Panel>
        </Tabs>
        {previewBar}
      </PageLayout.Container>
    </PageLayout>
  )
}

export const CommunityEmptyState = Object.assign(CommunityEmptyStateRoot, {
  Actions: EmptyStateBanner.Actions,
})
